import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';

const ProfileCompletionPrompt = ({
  open,
  onClose,
  title = 'Complete your profile',
  message = 'Please update your name, date of birth, and address to continue.',
  actionLabel = 'Complete Profile',
  PaperProps = {},
  keepMounted = false,
  icon = null,
  fields = null,
  secondaryAction = { label: 'Maybe later' },
  onPrimaryAction = null,
}) => {
  const navigate = useNavigate();
  const userDetails = useSelector((state) => state.userDetails.userDetails);
  // Debug: log userDetails so we can inspect what the prompt sees
  // Note: this will print sensitive user fields in the browser console while open.
  console.log('ProfileCompletionPrompt userDetails:', userDetails);

  const defaultPaper = {
    style: {
      background: 'linear-gradient(180deg, rgba(14,27,27,0.98) 0%, rgba(14,27,27,0.92) 100%)',
      color: 'white',
      borderRadius: 16,
      minWidth: 360,
      padding: 0,
      border: '1px solid rgba(255,255,255,0.06)',
      boxShadow: '0 20px 50px rgba(0,0,0,0.45)',
      backdropFilter: 'blur(10px)',
    },
  };

  const mergedPaper = { ...defaultPaper, ...PaperProps };

  const handleNavigate = () => {
    if (typeof onPrimaryAction === 'function') {
      onPrimaryAction();
      return;
    }
    onClose && onClose();
    navigate('/profile-completeness');
  };

  return (
    <Dialog open={open} onClose={onClose} PaperProps={mergedPaper} keepMounted={keepMounted}>
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        px: 2.5,
        py: 2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {icon ? (
            <Box sx={{ mr: 0.5 }}>{icon}</Box>
          ) : (
            <Box sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              background: 'linear-gradient(135deg,#0b6b68 0%, #3BB3AE 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: 22,
              boxShadow: 'inset 0 0 10px rgba(255,255,255,0.1)'
            }}>🧾</Box>
          )}
          <Box>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, letterSpacing: 0.2 }}>{title}</Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>Takes less than 2 minutes</Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} aria-label="close" size="small" sx={{ color: 'rgba(255,255,255,0.7)' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0, pb: 2, px: 2.5 }}>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.6 }}>{message}</Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {/* Determine which fields to show and their completion status. If caller provides `fields` prop, use it. Otherwise infer from user details. */}
            {((fields && fields.length > 0) ? fields : ['Name', 'Date of Birth', 'Address', 'Register your Face', 'Government ID']).map((f) => {
              // Determine completion based on known field keys
              let completed = true;
              const key = f.toLowerCase();
              if (fields && Array.isArray(fields) && !userDetails) {
                // when fields are passed but we don't have userDetails, assume completed
                completed = true;
              } else {
                if (key.includes('name')) {
                  completed = !!(userDetails?.firstName || userDetails?.lastName);
                } else if (key.includes('date')) {
                  completed = !!userDetails?.dateOfBirth;
                } else if (key.includes('address')) {
                  completed = Array.isArray(userDetails?.addresses) && userDetails.addresses.length > 0;
                } else if (key.includes('government') || key.includes('gov')) {
                  completed = userDetails?.governmentId !== null && userDetails?.governmentId !== undefined;
                } else if (key.includes('face')) {
                  // Consider face registered when `faceId` is present on the userDetails object
                  completed = !!userDetails?.faceId;
                } else {
                  completed = true;
                }
              }

              const bg = completed ? 'rgba(59,115,255,0.12)' : 'rgba(244,67,54,0.10)';
              const border = completed ? '1px solid rgba(59,115,255,0.28)' : '1px solid rgba(244,67,54,0.22)';
              const textColor = completed ? '#cfe0ff' : '#ffd6d6';

              return (
                <Box key={f} sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                  py: 0.5,
                  borderRadius: '999px',
                  background: bg,
                  border: border,
                  color: textColor,
                  fontSize: 12,
                  fontWeight: 600,
                }}>
                  {completed ? (
                    <CheckRoundedIcon sx={{ fontSize: 16, color: '#3B82F6' }} />
                  ) : (
                    <CloseRoundedIcon sx={{ fontSize: 16, color: '#F44336' }} />
                  )}
                  <span>{f}</span>
                </Box>
              );
            })}
          </Box>

          <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1, color: 'rgba(255,255,255,0.6)' }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#3BB3AE' }} />
            <Typography variant="caption">Unlock likes, comments, and voting after completing your profile.</Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, pt: 0, gap: 1.5, flexWrap: 'wrap' }}>
        {secondaryAction && (
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              color: 'rgba(255,255,255,0.9)',
              borderColor: 'rgba(255,255,255,0.2)',
              '&:hover': { borderColor: 'rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.04)' },
              minWidth: 140,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {secondaryAction.label}
          </Button>
        )}
        <Button
          onClick={handleNavigate}
          variant="contained"
          endIcon={<ArrowForwardRoundedIcon />}
          sx={{
            background: 'linear-gradient(135deg,#3BB3AE 0%, #00ffc8 100%)',
            '&:hover': { background: 'linear-gradient(135deg,#38a9a4 0%, #00e6b4 100%)' },
            minWidth: 170,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 700,
            letterSpacing: 0.2,
          }}
        >
          {actionLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProfileCompletionPrompt;


